/**
 * Generation APIs (try-on, video, polling)
 *
 * FLOW: transferClothes() -> POST /api/generate -> poll job -> fetch result image
 * TIMEOUTS: Generation = 3min, Video = 5min (configured in client.js)
 *
 * ERROR CODES from backend:
 * - 402: Payment required (subscription limit reached)
 * - 429: Rate limited (too many concurrent requests)
 * - 'queued': Server at capacity, user will be emailed when ready
 * - 'overloaded': AI temporarily unavailable, retry later
 */

import {
  apiCall,
  API_BASE,
  GENERATION_TIMEOUT,
  VIDEO_GENERATION_TIMEOUT,
  fetchAndConvertImage,
  pollStatus,
  getDevHeaders
} from './client'
import { ApiError } from './errors'

/**
 * Polls generation job status using shared pollStatus utility.
 * Handles special cases: queued, overloaded.
 */
async function pollJobStatus(jobId, token) {
  const devHeaders = getDevHeaders()

  return pollStatus({
    url: `${API_BASE}/api/generate/status/${jobId}`,
    getHeaders: () => ({ 'Authorization': `Bearer ${token}`, ...devHeaders }),
    timeout: GENERATION_TIMEOUT,
    isFailed: (data) => data.status === 'failed' || data.status === 'queued' || data.error_code === 'queued',
    onFailed: (data) => {
      if (data.status === 'queued' || data.error_code === 'queued') {
        return ApiError.queued(
          data.message || "We'll email you when your try-on is ready",
          { pendingId: data.pending_id }
        )
      }
      if (data.error_code === 'overloaded') {
        return ApiError.overloaded(data.error || "We're a bit busy - try again in a moment")
      }
      return new Error(data.error || 'Generation failed')
    },
    timeoutMessage: 'Generation timed out'
  })
}

/**
 * Polls video generation status. Uses fresh token each iteration since
 * video generation can take up to 5 minutes.
 */
async function pollVideoStatus(jobId, getToken) {
  return pollStatus({
    url: `${API_BASE}/api/generate/video/status/${jobId}`,
    getHeaders: async () => ({ 'Authorization': `Bearer ${await getToken()}` }),
    interval: 3000,
    timeout: VIDEO_GENERATION_TIMEOUT,
    onFailed: (data) => new Error(data.error || 'Video generation failed'),
    timeoutMessage: 'Video generation timed out'
  })
}

/**
 * Main try-on generation function. Triggers AI to composite clothing onto user's photo.
 * Handles polling internally - returns when generation is complete.
 *
 * PRIORITY: outfitId > wardrobeItemIds > pageUrl (only one used)
 */
export async function transferClothes(token, { outfitId, wardrobeItemIds, pageUrl }) {
  let body
  if (outfitId) body = { outfit_id: outfitId }
  else if (wardrobeItemIds?.length > 0) body = { wardrobe_item_ids: wardrobeItemIds }
  else body = { page_url: pageUrl }

  let result
  try {
    result = await apiCall('/api/generate', {
      token,
      method: 'POST',
      body,
      extraHeaders: getDevHeaders(),
      errorMessage: 'Generation failed'
    })
  } catch (err) {
    if (err.status === 402) {
      throw ApiError.paymentRequired(
        err.data?.message || 'Generation limit reached',
        { upgradeOptions: err.data?.upgrade_options || [] }
      )
    }
    if (err.status === 429) {
      // Pass through backend message - could be daily limit or rate limit
      throw ApiError.rateLimited(err.data?.message || "The fitting room's full — try again in a sec")
    }
    throw err
  }

  let finalResult
  if (result.job_id) finalResult = await pollJobStatus(result.job_id, token)
  else if (result.generation) finalResult = result
  else throw new Error('Unexpected response format')

  const imageData = await fetchAndConvertImage(finalResult.generation.result_url)

  return {
    success: true,
    imageData,
    generation: finalResult.generation,
    cached: finalResult.cached || false
  }
}

/**
 * Creates a video from an existing try-on image. Uses a different AI model.
 * Costs additional generation credits.
 */
export async function generateTryOnVideo(getToken, { generationId }) {
  const token = await getToken()

  let result
  try {
    result = await apiCall('/api/generate/video', {
      token,
      method: 'POST',
      body: { generation_id: generationId },
      timeout: VIDEO_GENERATION_TIMEOUT,
      errorMessage: 'Video generation failed'
    })
  } catch (err) {
    if (err.status === 402) {
      throw ApiError.paymentRequired(err.data?.message || 'Generation limit reached')
    }
    throw err
  }

  if (result.job_id) {
    const finalResult = await pollVideoStatus(result.job_id, getToken)
    return { success: true, videoUrl: finalResult.video_url, generation: finalResult.generation }
  }

  return { success: true, videoUrl: result.video_url, generation: result.generation }
}

/**
 * Delete a generation. Soft delete by default (can be restored).
 *
 * @param {string} token - Clerk auth token
 * @param {string} generationId - Generation ID to delete
 * @param {Object} [options]
 * @param {boolean} [options.permanent=false] - If true, permanently delete (no restore)
 * @returns {Promise<void>}
 * @throws {Error} If deletion fails
 */
export async function deleteGeneration(token, generationId, { permanent = false } = {}) {
  const endpoint = permanent ? `/api/generation/${generationId}?permanent=true` : `/api/generation/${generationId}`
  return apiCall(endpoint, { token, method: 'DELETE', errorMessage: 'Failed to delete generation', parseResponse: false })
}

/**
 * Restore a soft-deleted generation.
 *
 * @param {string} token - Clerk auth token
 * @param {string} generationId - Generation ID to restore
 * @returns {Promise<void>}
 * @throws {Error} If restoration fails
 */
export async function restoreGeneration(token, generationId) {
  return apiCall(`/api/generation/${generationId}/restore`, {
    token,
    method: 'POST',
    errorMessage: 'Failed to restore generation',
    parseResponse: false
  })
}
